import React, { useState } from "react";
import ColumnComponent from "../../ColumnComponent/ColumnComponent";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import OrderBox from "../../OrderBoxV2/OrderBoxV2";
import PerfectScrollbar from "react-perfect-scrollbar";
import filterIll from "../../../../assets/icons/filter-ilustration.svg";
import ArchivedColumnFilters from "./ArchivedColumnFilters";

type Props = {
  isAdmin?: boolean;
};

const ArchivedColumn = ({ isAdmin }: Props) => {
  const orderState = useSelector((state: RootState) => state.order);
  const rfzoTherapy = useSelector((state: RootState) => state.rfzoTherapy);

  const [filters, setFilters] = useState({
    status: "ALL",
  });

  // Filter items by status (client-side filtering)
  const filterByStatus = <T extends { status: string }>(
    items: T[],
    statuses: string
  ): T[] => {
    if (statuses === "ALL" || !statuses) return items;
    const statusList = statuses.split(",").filter(Boolean);
    if (statusList.length === 0) return items;
    return items.filter((item) => statusList.includes(item.status));
  };

  // Render finished items with status filtering
  const renderFinishedItems = () => {
    const filteredOrders = filterByStatus(
      orderState.finishedOrders,
      filters.status
    );
    return filteredOrders.map((item) => {
      const isTransaction = "orderNumber" in item;
      const key = isTransaction ? `transaction-${item.id}` : `order-${item.id}`;

      return (
        <OrderBox archived={true} key={key} order={item} isAdmin={isAdmin} />
      );
    });
  };

  // Render RFZO archived items with status filtering
  const renderRfzoItems = () => {
    const archivedRfzo = rfzoTherapy.rfzoTherapyRequests.filter(
      (request) =>
        request.status !== "REQUESTED" && request.status !== "PROCESSED"
    );
    const filteredRfzo = filterByStatus(archivedRfzo, filters.status);
    return filteredRfzo.map((rfzoRequest, index) => (
      <OrderBox
        archived={true}
        key={`rfzo-${rfzoRequest.id || index}`}
        rfzoRequest={rfzoRequest}
        isAdmin={isAdmin}
      />
    ));
  };

  // Handle loading states intelligently (like ReceivedColumn)
  const getDisplayContent = () => {
    const hasFinishedOrders = orderState.finishedOrders.length > 0;
    const hasRfzoArchived =
      rfzoTherapy.rfzoTherapyRequests.filter(
        (request) =>
          request.status !== "REQUESTED" && request.status !== "PROCESSED"
      ).length > 0;

    const hasAnyData = hasFinishedOrders || hasRfzoArchived;
    const isLoading =
      orderState.loadingOrders ||
      orderState.loadingTransactions ||
      rfzoTherapy.loadingRFZOTherapies;

    // Only show "Waiting..." if we have no data AND something is loading (initial load)
    if (!hasAnyData && isLoading) {
      return <div>Waiting...</div>;
    }

    // Show "No items" if we have no data and nothing is loading
    if (!hasAnyData && !isLoading) {
      return <div>Nema arhiviranih stavki</div>;
    }

    // Always show existing data (even during background polling)
    return (
      <>
        {renderFinishedItems()}
        {renderRfzoItems()}
      </>
    );
  };

  // Count calculation (reflects current filter)
  const archivedRfzo = rfzoTherapy.rfzoTherapyRequests.filter(
    (request) =>
      request.status !== "REQUESTED" && request.status !== "PROCESSED"
  );
  const filteredFinishedCount = filterByStatus(
    orderState.finishedOrders,
    filters.status
  ).length;
  const filteredRfzoCount = filterByStatus(archivedRfzo, filters.status).length;

  const totalArchivedCount = filteredFinishedCount + filteredRfzoCount;

  const [showFilters, setShowFilters] = useState(false);

  return (
    <ColumnComponent
      leftSideLabel="Obrađene"
      leftSideNumber={totalArchivedCount.toString()}
      // bodyStyle={{ backgroundColor: "transparent", border: "none" }}
      style={{ flex: "4" }}
    >
      <div
        onClick={() => setShowFilters(true)}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          cursor: "pointer",
        }}
      >
        <img width={24} src={filterIll} alt="Filter icon" />
      </div>
      {showFilters && (
        <ArchivedColumnFilters
          filters={filters}
          setFilters={setFilters}
          hideModal={() => setShowFilters(false)}
        />
      )}
      <PerfectScrollbar style={{ padding: "4px" }}>
        {getDisplayContent()}
      </PerfectScrollbar>
    </ColumnComponent>
  );
};

export default ArchivedColumn;
